import React from 'react';
import clsx from 'clsx';
import { CheckCircle2, XCircle, MinusCircle, TruckIcon } from 'lucide-react';
import { Button } from './Button';

const Stat = ({ icon: Icon, value, label, tone }) => (
  <div className="flex flex-col items-center gap-1.5 flex-1">
    <div
      className={clsx(
        'w-10 h-10 rounded-xl flex items-center justify-center',
        tone === 'success' && 'bg-ninja-greenLight text-ninja-green',
        tone === 'danger' && 'bg-red-100 text-danger dark:bg-red-900/20',
        tone === 'warning' && 'bg-orange-100 text-warning dark:bg-orange-900/20',
        tone === 'neutral' && 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted'
      )}
    >
      <Icon size={20} />
    </div>
    <span className="text-2xl font-extrabold text-ninja-dark dark:text-white leading-none">
      {value}
    </span>
    <span className="text-xs text-text-secondary text-center">{label}</span>
  </div>
);

/**
 * What happened to a batch, once it is over.
 *
 * A bulk action over N shipments rarely ends in one word. Closing the modal on
 * "listo" hides the half that did not go through, and reporting the whole run
 * as an error hides the half that did.
 *
 * **Skipped is not failed**, and they are counted apart on purpose: a shipment
 * the backend refuses to touch because of its status was never attempted, and
 * folding it into the failures makes a working action look broken.
 *
 * @param {object} props
 * @param {{requested: number, succeeded: array, failed: array, skipped?: array, dispatched?: array}} props.result
 * @param {string} [props.succeededLabel]
 * @param {string} [props.skippedLabel]
 * @param {() => void} props.onClose
 * @param {React.ReactNode} [props.children] - The detail of the run, drawn
 *   above the close button.
 */
export const BulkResult = ({
  result,
  succeededLabel = 'Listos',
  skippedLabel = 'Omitidos',
  onClose,
  children,
}) => {
  const succeeded = result.succeeded?.length ?? 0;
  const failed = result.failed?.length ?? 0;
  const skipped = result.skipped?.length ?? 0;
  const dispatched = result.dispatched?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-text-secondary text-center">
        Se procesaron {result.requested} {result.requested === 1 ? 'envío' : 'envíos'}.
      </p>

      <div className="flex items-start gap-2">
        <Stat icon={CheckCircle2} value={succeeded} label={succeededLabel} tone="success" />
        {skipped > 0 && (
          <Stat icon={MinusCircle} value={skipped} label={skippedLabel} tone="neutral" />
        )}
        <Stat icon={XCircle} value={failed} label="Con error" tone="danger" />
      </div>

      {/* Worth its own line: the change went through, but the parcel had
          already left, so there is nothing to walk back. */}
      {dispatched > 0 && (
        <div className="flex items-start gap-2.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-xl px-4 py-3">
          <TruckIcon size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-sm text-warning">
            {dispatched} {dispatched === 1 ? 'envío ya había salido' : 'envíos ya habían salido'} al
            courier, así que el cambio no se puede revertir.
          </p>
        </div>
      )}

      {children}

      <div className="flex justify-end">
        <Button variant="primary" pill onClick={onClose}>
          Entendido
        </Button>
      </div>
    </div>
  );
};
